import { Badge, Box, Circle, HStack, Menu, Portal, Separator, Spacer, Stack, Tabs, Text } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'

import Settings from './nav/settings'

import { Button } from '@app/components/ui/button'
import useGetObject from '@app/hooks/useGetObject'
import useIsAnonymousUser from '@app/hooks/useIsAnonymousUser'
import BulkImportStatusModal from '@app/pages/strategies/components/map/nav/bulkImportStatusModal'
import UserPresences from '@app/pages/strategies/components/map/nav/userPresences'
import NavMobilePopover from '@app/pages/strategies/components/map/navMobilePopover'
import PlaybookCreateButton from '@app/pages/strategies/components/playbookCreateButton'
import SharingDialog from '@app/pages/strategies/components/sharing/sharingDialog'
import { useDrawerContext } from '@app/pages/strategies/drawerContext'
import { useStrategyMapContext } from '@app/pages/strategies/strategyMapContext'
import FavoriteToggleButton from '@app/shared/favorites/favoriteToggleButton'
import { ToggleButton } from '@app/shared/layout/shells/components/toggleButton'
import type { MapMode } from '@app/types'
import type { Strategy } from '@graphql/types'

interface Props {
  strategyId: string
  embedded?: boolean
}

interface TitleProps {
  as?: typeof Link
  to?: string
}
const Nav: FC<Props> = ({ strategyId, embedded = false }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { canEdit, mapMode, setMapMode } = useStrategyMapContext()
  const isAnonymousUser = useIsAnonymousUser(strategyId)
  const { isDrawerOpen, onDrawerToggle } = useDrawerContext()
  const strategy = useGetObject(strategyId, 'strategy')

  if (!strategy) {
    return null
  }

  const { id, favoriteId, recurringReportConfigurationsCount } = strategy
  const stackSpacing = { base: 2, md: 4 }
  const titleProps: TitleProps = {}

  if (!isAnonymousUser) {
    titleProps.as = Link
    titleProps.to = 'map/settings'
  }

  const navActive = (values: MapMode[]): boolean => values.includes(mapMode)
  const navigateToBuild = () => {
    if (location.pathname.includes('map')) {
      return
    }

    navigate(`/strategy/${strategy.id}/map`)
  }

  const handleChange = (e) => {
    navigateToBuild()
    setMapMode(e.value)
  }

  const DesktopNavBar = () => {
    if (isAnonymousUser) {
      return null
    }

    return (
      <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
        <Tabs.Root
          variant="enclosed"
          value={mapMode === 'simulation' ? 'build' : mapMode}
          onValueChange={handleChange}
          size="sm"
        >
          <Tabs.List>
            <Tabs.Trigger value="build">{canEdit && 'Build'} Map</Tabs.Trigger>
            {canEdit && (
              <>
                <Tabs.Trigger value="data">Connect Data</Tabs.Trigger>
                <Tabs.Trigger value="goals">Set Goals</Tabs.Trigger>
              </>
            )}
            <Tabs.Trigger value="reports" asChild>
              <Menu.Root>
                <Menu.Trigger asChild>
                  <Button
                    bg={navActive(['reports']) ? 'bg' : 'bg.muted'}
                    color={navActive(['reports']) ? 'gray.fg' : 'fg.muted'}
                    size="sm"
                  >
                    Reports
                    {recurringReportConfigurationsCount > 0 && (
                      <Circle bg="bg" size="14px" outline="0.1em solid" outlineColor="purple.500">
                        <Text fontSize="2xs" fontWeight="semibold" color="fg.muted">
                          {recurringReportConfigurationsCount}
                        </Text>
                      </Circle>
                    )}
                    <FiChevronDown />
                  </Button>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.Item value="ai_reports">
                        <NavLink to="dashboard" onClick={() => setMapMode('reports')}>
                          AI reports <Badge colorPalette="purple">Beta</Badge>
                        </NavLink>
                      </Menu.Item>
                      <Menu.Item value="key_results" onClick={() => setMapMode('reports')}>
                        <NavLink to="key-results">Key results</NavLink>
                      </Menu.Item>
                      <Menu.Item value="changelog" onClick={() => setMapMode('reports')}>
                        <NavLink to="changelog">Changelog</NavLink>
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>
        <BulkImportStatusModal strategyId={strategyId} />
      </Stack>
    )
  }

  return (
    <Box zIndex="modal" px={2} py={1} bg="bg" borderRadius="sm" shadow="sm">
      <HStack gap={stackSpacing}>
        <HStack flexGrow={0} gap={1}>
          <ToggleButton
            fontSize="lg"
            variant="ghost"
            open={isDrawerOpen}
            aria-label="Open Menu"
            onClick={onDrawerToggle}
            show={!isAnonymousUser}
          />
          <Text px={1} py={2} fontSize="sm" defaultValue={strategy?.name} {...titleProps}>
            {strategy?.name}
          </Text>
        </HStack>
        <Separator h="1.5rem" ml={2} orientation="vertical" />
        {!embedded && (
          <HStack flexGrow={1} flexShrink={0} hideBelow="md">
            <DesktopNavBar />
          </HStack>
        )}
        <Spacer />
        <UserPresences />
        <NavMobilePopover>
          <DesktopNavBar />
        </NavMobilePopover>
        <HStack gap={1} hideBelow="md">
          <FavoriteToggleButton
            favoritableType="Strategy"
            favoritableId={id}
            favoriteId={favoriteId}
            iconBoxSize={5}
            show={!isAnonymousUser}
            colorPalette="gray"
          />
          <PlaybookCreateButton strategy={strategy} show={canEdit} />
          <Settings strategy={strategy as Strategy} show={canEdit} />
          <SharingDialog strategy={strategy as Strategy} show={canEdit} />
        </HStack>
      </HStack>
    </Box>
  )
}

export default memo(Nav)
